// API client for the admin dashboard. The actual auth + role check
// happens server-side; these helpers just hit /admin/* and surface the
// JSON. Network/permission failures return null so callers can render
// "couldn't load" without juggling Error objects.

import { apiFetch } from './api';

export interface AdminStatsResponse {
  visits: { day: string; web: number; ios: number }[];
  // Today only, bucketed by NY-local hour 0-23. Sums to today's totals
  // in `visits`. Powers the Overview Day view.
  hourlyToday: { hour: number; web: number; ios: number }[];
  subscriptions: {
    total: number;
    premium: number;
    free: number;
    premiumMonthly: number;
    premiumYearly: number;
  };
}

export async function fetchAdminStats(): Promise<AdminStatsResponse | null> {
  try {
    const res = await apiFetch('/admin/stats');
    if (!res.ok) return null;
    return (await res.json()) as AdminStatsResponse;
  } catch { return null; }
}

export interface AdminUserRow {
  id: string;
  email: string;
  displayName: string;
  role: string;
  emailVerified: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  subscription: {
    plan: string;
    status: string;
    currentPeriodEnd: string | null;
    source: 'stripe' | 'apple' | 'manual';
  } | null;
}

export interface AdminUsersResponse {
  total: number;
  offset: number;
  limit: number;
  users: AdminUserRow[];
}

export async function fetchAdminUsers(args: {
  q?: string; offset?: number; limit?: number;
}): Promise<AdminUsersResponse | null> {
  try {
    const params = new URLSearchParams();
    if (args.q)      params.set('q', args.q);
    if (args.offset) params.set('offset', String(args.offset));
    if (args.limit)  params.set('limit', String(args.limit));
    const qs = params.toString();
    const res = await apiFetch(`/admin/users${qs ? '?' + qs : ''}`);
    if (!res.ok) return null;
    return (await res.json()) as AdminUsersResponse;
  } catch { return null; }
}

export interface GrantPremiumResult {
  ok: boolean;
  error?: string;
}

export async function grantPremium(
  userId: string,
  plan: 'premium_monthly' | 'premium_yearly',
  durationDays?: number,
): Promise<GrantPremiumResult> {
  try {
    const res = await apiFetch(`/admin/users/${encodeURIComponent(userId)}/premium`, {
      method: 'POST',
      body: JSON.stringify({ plan, durationDays }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: typeof data.error === 'string' ? data.error : 'unknown' };
    return { ok: true };
  } catch { return { ok: false, error: 'network' }; }
}

export async function revokePremium(userId: string): Promise<GrantPremiumResult> {
  try {
    const res = await apiFetch(`/admin/users/${encodeURIComponent(userId)}/premium`, {
      method: 'DELETE',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: typeof data.error === 'string' ? data.error : 'unknown' };
    return { ok: true };
  } catch { return { ok: false, error: 'network' }; }
}
